<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import BaseButton from '@/components/ui/BaseButton.vue';
import BaseModal from '@/components/ui/BaseModal.vue';
import { ApiRequestError } from '@/lib/api';
import {
  EMERGENCY_STOP_CLEAR_CONFIRM,
  EMERGENCY_STOP_CONFIRM,
  describeEmergencyStopFailure,
  describeEmergencyStopOutcome,
  type ActionOutcome,
  type EmergencyStopAction,
} from '@/lib/comment-view';
import { formatDateTime } from '@/lib/format';
import type { Tone } from '@/lib/labels';
import { useAgentsStore } from '@/stores/agents';
import { useAuthStore } from '@/stores/auth';
import { useUiStore, type ToastKind } from '@/stores/ui';

/**
 * The application chrome: navigation, the realtime connection indicator, the
 * emergency-stop switch and sign-out.
 *
 * The realtime indicator is not decoration. Every live signal on this dashboard
 * — agent online, Facebook session, project status — arrives over that socket, so
 * a user looking at a stale screen has to be able to see that it is stale.
 *
 * WHY THE KILL SWITCH LIVES IN THE CHROME. Three places in this app tell a
 * frightened operator to "bật Emergency Stop — thứ duy nhất dừng được một lượt
 * gõ đang chạy": the cancel prompt on the comment queue, the DISPATCHED notice
 * in the draft dialog, and `describeCancelOutcome`. Until this round nothing in
 * the dashboard called `POST /comments/emergency-stop` at all — this file drew
 * the banner for a switch it could not throw, and the advice pointed at a button
 * that did not exist. The button belongs here rather than on the comment screen
 * because the workspace is what stops, not the queue: whoever needs it is
 * looking at whatever screen the bad news arrived on, and this bar is on all of
 * them. The comment queue names it by label and by location instead of growing a
 * second copy — two buttons for one irreversible action is how the two dialogs
 * that describe it drift apart.
 */

const auth = useAuthStore();
const ui = useUiStore();
const agents = useAgentsStore();
const router = useRouter();

const {
  realtimeStatus,
  emergencyStopActive,
  emergencyStopReason,
  emergencyStopAt,
  emergencyStopBusy,
} = storeToRefs(ui);
const { profilesNeedingUserAction } = storeToRefs(agents);

/**
 * SelectorPack and Prompt AI are calibration surfaces whose write routes are
 * `@Roles('ADMIN')` server-side; hiding them for MEMBER spares that user a
 * screen of buttons that can only 403. The server remains the gate — this is
 * presentation, not enforcement.
 */
const navItems = computed(() => {
  const admin = auth.user?.role === 'ADMIN';
  return [
    { to: '/projects', label: 'Project' },
    { to: '/agents', label: 'Agent & trình duyệt' },
    { to: '/posts', label: 'Bài viết' },
    { to: '/comment-jobs', label: 'Bình luận' },
    ...(admin
      ? [
          { to: '/selector-packs', label: 'SelectorPack' },
          { to: '/ai-prompts', label: 'Prompt AI' },
        ]
      : []),
    { to: '/settings', label: 'Cài đặt' },
  ];
});

const realtimeLabel = computed(() => {
  switch (realtimeStatus.value) {
    case 'open':
      return 'Realtime: đang kết nối';
    case 'connecting':
      return 'Realtime: đang mở kết nối…';
    case 'reconnecting':
      return 'Realtime: mất kết nối, đang thử lại…';
    case 'closed':
      return 'Realtime: đã ngắt';
    default:
      return 'Realtime: chưa bật';
  }
});

const realtimeTone = computed(() =>
  realtimeStatus.value === 'open'
    ? 'ok'
    : realtimeStatus.value === 'reconnecting'
      ? 'warn'
      : 'off',
);

// ---------------------------------------------------------------------------
// Emergency Stop
// ---------------------------------------------------------------------------

/** `Tone` describes a colour, `ToastKind` picks a toast; `danger` is the only rename. */
const TOAST_KIND_BY_TONE: Record<Tone, ToastKind> = {
  neutral: 'info',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
};

/**
 * Mirrors `emergencyStopRequestSchema` in `apps/api/src/comments/comments.service.ts`
 * (`z.string().trim().max(500)`). That DTO never moved into the contract package,
 * so there is nothing to import; capping the textarea here means a reason typed
 * one character too long is stopped by the field rather than by a 422 on the one
 * request that must not bounce.
 */
const EMERGENCY_STOP_REASON_MAX = 500;

/**
 * The dialog's paragraphs, taken apart from the shared constant rather than
 * retyped. `EMERGENCY_STOP_CONFIRM` is written for `ui.confirm`, and everything
 * except the reason field is used exactly as it stands — see {@link openStopPrompt}.
 */
const STOP_PARAGRAPHS = EMERGENCY_STOP_CONFIRM.message.split('\n\n');

const stopPromptOpen = ref(false);
const stopReason = ref('');

/**
 * True when this session's own role cannot release the switch.
 *
 * Said in the banner rather than used to disable the button. The role here comes
 * from the last `/auth/me`, and a member promoted to admin five minutes ago would
 * be left staring at a dead control with no explanation; the server is the
 * authority and `describeEmergencyStopFailure` already turns its 403 into a
 * sentence that names the people who can help. Warning up front only means the
 * refusal is not a surprise.
 */
const clearNeedsPrivilege = computed(() => auth.user?.role === 'MEMBER');

function report(outcome: ActionOutcome): void {
  ui.pushToast(TOAST_KIND_BY_TONE[outcome.tone], outcome.title, outcome.detail);
}

function reportFailure(caught: unknown, action: EmergencyStopAction): void {
  // Both store actions funnel their failure through `toApiError`, so this is
  // always an `ApiRequestError`; the guard is what keeps that a checked fact
  // rather than an assumption written as a type.
  if (caught instanceof ApiRequestError) {
    report(describeEmergencyStopFailure(caught, action));
    return;
  }
  ui.notifyError('Không thao tác được với dừng khẩn cấp', caught);
}

/**
 * Ask before stopping, in a dialog of our own rather than through `ui.confirm`.
 *
 * `ConfirmHost` renders a message and two buttons and has nowhere to type. The
 * reason matters: it is the only thing that tells the next person to open the
 * dashboard why automation is down, and the clear dialog tells them not to
 * release the switch until they know. It is optional and the field says so —
 * a kill switch that demands a form before it fires is not a kill switch.
 */
function openStopPrompt(): void {
  stopReason.value = '';
  stopPromptOpen.value = true;
}

function closeStopPrompt(): void {
  stopPromptOpen.value = false;
  stopReason.value = '';
}

async function confirmStop(): Promise<void> {
  try {
    report(describeEmergencyStopOutcome(await ui.activateEmergencyStop(stopReason.value)));
    closeStopPrompt();
  } catch (caught) {
    // Left open, with the typed reason still in it. The failure copy says "thử
    // lại ngay", and a dialog that closed on failure would make the retry start
    // with retyping — at the moment retyping costs the most.
    reportFailure(caught, 'ACTIVATE');
  }
}

async function clearStop(): Promise<void> {
  const accepted = await ui.confirm(EMERGENCY_STOP_CLEAR_CONFIRM);
  if (!accepted) return;

  try {
    report(describeEmergencyStopOutcome(await ui.clearEmergencyStop()));
  } catch (caught) {
    reportFailure(caught, 'CLEAR');
  }
}

async function signOut(): Promise<void> {
  const accepted = await ui.confirm({
    title: 'Đăng xuất',
    message: 'Bạn có chắc muốn đăng xuất khỏi trình duyệt này?',
    confirmLabel: 'Đăng xuất',
  });
  if (!accepted) return;

  await auth.logout();
  await router.push({ name: 'login' });
}
</script>

<template>
  <div class="shell">
    <aside class="sidebar">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">◑</span>
        <span>MidiAutoListening</span>
      </div>

      <nav>
        <RouterLink v-for="item in navItems" :key="item.to" :to="item.to" class="nav-link">
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="sidebar-foot">
        <p class="user">{{ auth.displayName }}</p>
        <BaseButton size="sm" block @click="signOut">Đăng xuất</BaseButton>
      </div>
    </aside>

    <div class="main">
      <header class="topbar">
        <!-- Hidden only while the switch is known to be on: the banner below
             then carries the release button instead. When the state has not
             been read at all this still shows, because pressing stop on a
             workspace that is already stopped costs an audit row, and not
             offering it costs whatever gets typed in the meantime. -->
        <BaseButton
          v-if="!emergencyStopActive"
          size="sm"
          variant="danger"
          :loading="emergencyStopBusy"
          title="Dừng toàn bộ automation của workspace. Ai cũng bấm được."
          @click="openStopPrompt"
        >
          Dừng khẩn cấp
        </BaseButton>

        <span class="realtime" :class="realtimeTone">
          <span class="dot" aria-hidden="true" />
          {{ realtimeLabel }}
        </span>
      </header>

      <div
        v-if="emergencyStopActive"
        class="banner danger"
        role="alert"
      >
        <div class="banner-text">
          <strong>Emergency Stop đang bật.</strong>
          Mọi automation của workspace bị giữ lại cho tới khi tắt công tắc này.
          <span v-if="emergencyStopReason"> Lý do: “{{ emergencyStopReason }}”.</span>
          <!-- `emergencyStopAt` is the moment the switch was thrown, read from
               the `comment.emergency_stop` audit row — it used to be the comment
               settings' `updatedAt`, which any unrelated settings save moved to
               the current minute. It is null when no such row exists, and then
               this says so rather than printing a number that looks measured. -->
          <span v-if="emergencyStopAt"> Bật lúc {{ formatDateTime(emergencyStopAt) }}.</span>
          <span v-else> Không rõ bật lúc nào — máy chủ không có bản ghi thời điểm cho lần bật này.</span>
          <span v-if="clearNeedsPrivilege">
            Tắt là quyền của chủ workspace hoặc quản trị viên.
          </span>
        </div>

        <BaseButton size="sm" :loading="emergencyStopBusy" @click="clearStop">
          Tắt dừng khẩn cấp
        </BaseButton>
      </div>

      <div v-if="profilesNeedingUserAction.length > 0" class="banner warn" role="alert">
        <strong>{{ profilesNeedingUserAction.length }} profile cần bạn xử lý thủ công.</strong>
        Hệ thống cố ý không tự vượt CAPTCHA / checkpoint / 2FA.
        <RouterLink to="/agents">Mở màn hình Agent →</RouterLink>
      </div>

      <main class="content">
        <slot />
      </main>
    </div>

    <!-- `tier="confirm"` for the same reason ConfirmHost uses it: this asks
         whether an irreversible thing should happen, so it may never end up
         underneath an ordinary modal. `persistent` so a stray backdrop click
         cannot throw away a half-typed reason. -->
    <BaseModal
      :open="stopPromptOpen"
      :title="EMERGENCY_STOP_CONFIRM.title"
      width="520px"
      persistent
      tier="confirm"
      @close="closeStopPrompt"
    >
      <p v-for="(paragraph, index) in STOP_PARAGRAPHS" :key="index" class="stop-line">
        {{ paragraph }}
      </p>

      <label class="stop-reason">
        <span class="field-label">Lý do — không bắt buộc, bỏ trống vẫn bấm được</span>
        <textarea
          v-model="stopReason"
          class="textarea"
          rows="2"
          :maxlength="EMERGENCY_STOP_REASON_MAX"
          placeholder="Ví dụ: agent đang gõ nhầm bình luận lên bài của khách."
        />
        <span class="field-hint">
          Lý do hiện trên dải cảnh báo của mọi màn hình đang mở và được ghi vào nhật ký kiểm toán.
          Nó không sống sót qua một lần tải lại trang, nên đừng để đây là nơi duy nhất ghi lại
          chuyện đã xảy ra.
        </span>
      </label>

      <template #footer>
        <BaseButton @click="closeStopPrompt">
          {{ EMERGENCY_STOP_CONFIRM.cancelLabel ?? 'Huỷ' }}
        </BaseButton>
        <BaseButton variant="danger" :loading="emergencyStopBusy" @click="confirmStop">
          {{ EMERGENCY_STOP_CONFIRM.confirmLabel ?? 'Dừng khẩn cấp' }}
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: 232px 1fr;
  min-height: 100vh;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 16px 12px;
  border-right: 1px solid var(--app-border);
  background: var(--app-surface);
  position: sticky;
  top: 0;
  height: 100vh;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  font-weight: 700;
  font-size: 14px;
}

.brand-mark {
  color: var(--app-primary);
  font-size: 18px;
}

nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.nav-link {
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--app-text);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
}

.nav-link:hover {
  background: var(--app-surface-2);
  text-decoration: none;
}

.nav-link.router-link-active {
  background: color-mix(in srgb, var(--app-primary) 18%, transparent);
  color: var(--app-primary);
}

.sidebar-foot {
  border-top: 1px solid var(--app-border);
  padding-top: 12px;
}

.user {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--app-muted);
  overflow-wrap: anywhere;
}

.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--app-border);
}

.realtime {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--app-muted);
}

.realtime .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--app-muted);
}

.realtime.ok .dot {
  background: var(--app-success);
}

.realtime.warn .dot {
  background: var(--app-warning);
}

.realtime.off .dot {
  background: var(--app-danger);
}

.banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding: 10px 20px;
  font-size: 13px;
  border-bottom: 1px solid var(--app-border);
}

/* The button must not be pushed off the row by a long reason string. */
.banner-text {
  flex: 1 1 320px;
  min-width: 0;
}

.stop-line {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.5;
}

.stop-reason {
  display: block;
  margin-top: 4px;
}

.banner.danger {
  background: color-mix(in srgb, var(--app-danger) 14%, transparent);
  color: var(--app-danger);
}

.banner.warn {
  background: color-mix(in srgb, var(--app-warning) 14%, transparent);
  color: var(--app-warning);
}

.content {
  padding: 20px;
  min-width: 0;
}

@media (max-width: 860px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: static;
    height: auto;
  }

  nav {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
